import {
  OfferScreen, OfferTriggerRules, OfferTriggersJson, OfferTriggersState, TriggerRule, UpgradeTarget, offerOf, offersAllowed,
} from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { HasSections, registerSaveSection } from "./saveSections";
import type { DealsContext, PageView } from "./dealsTypes";

/**
 * Offers suggested from the save (MONETISATION_PLAN MP-05): plain rules that BADGE an offer already on sale — they never
 * open anything, never show in a level or on the end screen, and stay quiet for 10 minutes after a loss (the never
 * list, domain `offersAllowed`). Each rule badges at most once a UTC day; its badge clears when the player views what it
 * points at (the Deals page, or the Shop's Gems page for gold packs).
 *
 * - **loss**: the same level lost `lossStreak` times in a row → today's Armourer deals (Daily Deals), else an unbought
 *   Chapter Pack, else Daily Deals when Change can still pick the Armourer.
 * - **gold**: from `goldFromLevel`, gold below the cheapest next upgrade → the Shop's gold packs (gems), while one is left today.
 * - **mythic**: reaching `mythicFromLevel` (the Mythic rarity's first level) and each chapter after → the Master's Collection.
 * - **welcome**: back after the check-in's welcome-back days → the Welcome Back pack, on sale `welcomeHours` from the
 *   return, once per return, at most once every `welcomeEveryDays`.
 *
 * State: the `offerTriggers` save section (domain OfferTriggersState).
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const offerTriggersSection = registerSaveSection<OfferTriggersJson>("offerTriggers", asObject, (v) => v);

/** Where a suggestion points: a Deals page, or the Shop's gold packs. */
export const GOLD_PACKS = "shop:goldPacks";
/** Daily Deals, its gear theme, Chapter Packs and the Master's Collection (offers.json page and theme ids). */
const DAILY = "daily", ARMOURER = "armourer", CHAPTER = "chapter", MASTERS = "masters";

/** One live suggestion: the rule and what it badges. */
export interface Suggestion { readonly rule: TriggerRule; readonly target: string }

/** What the hub badges now: Deals pages (each one dot) and the Shop's gold packs. */
export interface Suggestions { readonly deals: readonly string[]; readonly goldPacks: boolean }

const NONE: Suggestions = { deals: [], goldPacks: false };

export class OfferTriggers {
  constructor(private readonly catalog: LevelCatalog, readonly state: OfferTriggersState) {}

  static stateFromSave(save: HasSections): OfferTriggersState { return OfferTriggersState.fromJson(offerTriggersSection.read(save)); }
  applyTo(save: HasSections): void { offerTriggersSection.write(save, this.state.toJson()); }

  get rules(): OfferTriggerRules { return this.catalog.balance.offers.triggers; }

  /** A battle's result (LevelCatalog.recordResult): a level's run of losses. */
  noteResult(level: number, won: boolean, now: number): void { this.state.noteResult(level, won, now); }

  /** A hub visit: a return the check-in's welcome-back gift is due for opens the Welcome Back window. Returns whether it opened. */
  refresh(now: number): boolean {
    return this.catalog.checkin.welcomePending && this.state.openWelcome(this.rules, now);
  }

  // ------------------------------------------------------------------ the Welcome Back pack

  /** Whether the Welcome Back pack is on sale now (Deals.view's gate for `welcomeBack` offers). */
  welcomeOnSale(now: number): boolean { return this.state.welcomeOnSale(this.rules, now); }
  /** The end of the current window (the page's countdown); null when none ever opened. */
  welcomeEndsAt(): number | null { return this.state.welcomeEndsAt(this.rules); }
  /** A granted purchase (Deals.grant): the Welcome Back pack spends its window. */
  noteBought(offerId: string, at: number): void {
    if (offerOf(this.catalog.balance.offers, offerId)?.welcomeBack) this.state.noteWelcomeBought(at);
  }

  // ------------------------------------------------------------------ badges

  /** The live suggestions (none outside the hub, or in the quiet after a loss). */
  active(now: number, ctx: DealsContext, screen: OfferScreen = "hub"): Suggestion[] {
    if (!offersAllowed(this.catalog.balance, screen, now, this.catalog.economy.shop.lastLossAt)) return [];
    const pages = this.catalog.deals.pages(now, ctx), out: Suggestion[] = [];
    const loss = this.state.lossLevel(this.rules, now) > 0 ? lossTarget(pages) : null;
    if (loss) out.push({ rule: "loss", target: loss });
    if (!this.state.seenToday("gold", now) && this.goldShort(now)) out.push({ rule: "gold", target: GOLD_PACKS });
    const mythic = this.mythicChapter();
    if (mythic >= 0 && this.state.mythicDue(mythic) && !this.state.seenToday("mythic", now) && pages.some((p) => p.page.id === MASTERS)) out.push({ rule: "mythic", target: MASTERS });
    const welcome = pages.find((p) => p.offers.some((o) => offerOf(this.catalog.balance.offers, o.offerId)?.welcomeBack));
    if (welcome && !this.state.welcomeSeen()) out.push({ rule: "welcome", target: welcome.page.id });
    return out;
  }

  /** The hub's badges: the Deals pages suggested (each once) and whether the gold packs are. */
  suggestions(now: number, ctx: DealsContext, screen: OfferScreen = "hub"): Suggestions {
    const a = this.active(now, ctx, screen);
    if (a.length === 0) return NONE;
    return { deals: [...new Set(a.filter((s) => s.target !== GOLD_PACKS).map((s) => s.target))], goldPacks: a.some((s) => s.target === GOLD_PACKS) };
  }

  /** The player viewed `target` (a Deals page id, or GOLD_PACKS): the rules pointing at it are seen today. Returns whether anything cleared. */
  viewed(target: string, now: number, ctx: DealsContext): boolean {
    const hits = this.active(now, ctx).filter((s) => s.target === target);
    for (const s of hits) {
      this.state.markSeen(s.rule, now);
      if (s.rule === "mythic") this.state.markMythicSeen(this.mythicChapter());
    }
    return hits.length > 0;
  }

  /** The 0-based chapter of the furthest level reached, when it is Mythic-tier (−1: not yet). */
  private mythicChapter(): number {
    const c = this.catalog, reached = c.nextLevel;
    return reached >= this.rules.mythicFromLevel ? c.chapterOfLevel(reached) : -1;
  }

  /** From `goldFromLevel`: gold below the cheapest next upgrade, while a gold pack can still be bought today. */
  private goldShort(now: number): boolean {
    const c = this.catalog, level = c.highestLevelCleared;
    if (level < this.rules.goldFromLevel) return false;
    const next = nextUpgradeCost(c);
    if (next === null || c.profile.gold >= next) return false;
    return c.balance.commerce.shop.resourcePacks.some((p) => level >= p.unlockAfterLevel && c.economy.shop.packsBought(now, p.id) < p.perDay);
  }
}

/** Where a run of losses points: today's Armourer deals, else an unbought Chapter Pack, else Daily Deals if Change can still pick the Armourer. */
function lossTarget(pages: readonly PageView[]): string | null {
  const daily = pages.find((p) => p.page.id === DAILY), open = daily?.offers.some((o) => o.blocked === "") ?? false;
  if (daily && open && daily.theme?.id === ARMOURER) return DAILY;
  if (pages.some((p) => p.page.id === CHAPTER)) return CHAPTER;
  const canPick = daily?.page.daily?.themes.some((t) => t.id === ARMOURER) ?? false;
  return daily && open && canPick && (daily.theme?.changesLeft ?? 0) > 0 ? DAILY : null;
}

/** The cheapest next upgrade of the fortress or an owned tower not already upgrading (null: everything is at its peak). */
export function nextUpgradeCost(c: LevelCatalog): number | null {
  const B = c.balance, p = c.profile, owned = c.ownedTowers();
  let best: number | null = null;
  B.towers.forEach((t, i) => {
    if (!t.isBase && !owned[i]) return;
    if (p.isUpgrading(t.isBase ? UpgradeTarget.Base : UpgradeTarget.Tower, i)) return;
    const level = t.isBase ? p.baseLevel : p.towerLevels[i];
    if (level >= B.tables.maxLevel) return;
    const cost = p.costFor(level + 1);
    if (best === null || cost < best) best = cost;
  });
  return best;
}
